import { useQuery } from "@tanstack/react-query";
import { apiClient } from "./client";
import type { LessonAttachment } from "./content";
import type { ContentLessonPayload } from "@/features/content-blocks/types";

// ─── Types (mirror backend serializers) ────────────────────────────────────

export interface Tag {
  id: number;
  name: string;
  slug: string;
}

export interface CourseListItem {
  id: number;
  code: string;
  slug: string;
  name: string;
  description: string;
  credits: number;
  price_cents: number;
  // Sprint 26+ — flat = price_cents; per_credit = TuitionRate hiện hành × credits.
  pricing_mode: "flat" | "per_credit";
  // Học phí THỰC TẾ dùng để hiển thị/enroll (xem BE `resolve_course_price_cents`).
  // `null` nếu per_credit mà tenant chưa thiết lập TuitionRate nào.
  effective_price_cents: number | null;
  cover_image: string | null;
  is_published: boolean;
  tags: Tag[];
  lesson_count: number;
  term_names: string[];
}

export interface LessonListItem {
  id: number;
  slug: string;
  title: string;
  order: number;
  duration_minutes: number;
  is_preview: boolean;
}

export interface ModuleSummary {
  id: number;
  slug: string;
  title: string;
  order: number;
  description: string;
  lessons: LessonListItem[];
}

export interface CourseDetail extends Omit<CourseListItem, "lesson_count"> {
  lessons: LessonListItem[];
  /** ja|en|zh|ko — môn ngôn ngữ (2026-10-03: hiện "Luyện giao tiếp"). */
  speaking_language?: string;
  // Sprint 28 — Chương. Empty cho course phẳng (legacy / chưa publish AI Designer).
  modules: ModuleSummary[];
  // Sprint 28 M2b — versioning
  version: number;
  is_latest: boolean;
  created_at: string;
  updated_at: string;
}

// ─── Language course payload (Pha 1 — renderer tương tác) ──────────────────
// Mirror schema importer (backend/apps/catalog/services/language_import.py).
// Tất cả optional — renderer xử lý graceful khi thiếu trường.
export interface LangModelSentence {
  ja: string;
  furigana?: string;
  romaji?: string;
  vi?: string;
  // Pha 2 — URL audio TTS (model_sentences). Thiếu = nút loa disabled.
  audio?: string;
}
export interface LangVocab {
  ja: string;
  furigana?: string;
  romaji?: string;
  vi?: string;
  pos?: string;
  // Pha 2 — URL audio TTS (vocab). Thiếu = nút loa disabled.
  audio?: string;
}
export interface LangKanji {
  char: string;
  on?: string;
  kun?: string;
  vi?: string;
}
export interface LangSpeaking {
  prompt_vi?: string;
  target_ja?: string;
  // Pha 2b — URL audio mẫu cho câu đích (generate_lesson_audio --with-speaking).
  audio?: string;
}
export interface LangSrsCard {
  front_ja?: string;
  back_vi?: string;
}
export interface LanguageLessonPayload {
  lesson_id?: string;
  module?: number;
  title_vi?: string;
  title_ja?: string;
  level?: string;
  duration_min?: number;
  grammar_point?: { form?: string; meaning_vi?: string; note_vi?: string };
  hook?: { scene_vi?: string; audio_script_ja?: string; image_hint?: string };
  model_sentences?: LangModelSentence[];
  vocab?: LangVocab[];
  kanji?: LangKanji[];
  speaking?: LangSpeaking[];
  culture_note?: { vi?: string };
  srs_cards?: LangSrsCard[];
}

export interface ScormPackageMeta {
  id: number;
  title: string;
  scorm_version: string;
}

export interface LessonDetail extends LessonListItem {
  content: string;
  // Pha 1 — schema bài ngôn ngữ cho renderer tương tác. null = markdown thường.
  // 2026-10-27 — hoặc bài theo khối của gói nội dung F-Future (`ffuture.lesson/1`).
  structured_payload?: LanguageLessonPayload | ContentLessonPayload | null;
  course_slug: string;
  course_name: string;
  /** ja|en|zh|ko — môn ngôn ngữ (2026-10-03). */
  course_speaking_language?: string;
  // Navigation (Sprint 3 B6)
  prev_slug: string | null;
  next_slug: string | null;
  // Video meta (Sprint 3 B6) — playback URL fetch riêng qua /api/content/
  has_video: boolean;
  video_ready: boolean;
  video_status: "pending" | "uploading" | "processing" | "ready" | "error";
  video_thumbnail_url: string;
  // Sprint 30 — SCORM player
  scorm_package?: ScormPackageMeta | null;
  // 2026-10-02 — tài liệu đính kèm PDF/PowerPoint/Word
  attachments?: LessonAttachment[];
  created_at: string;
  updated_at: string;
}

export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// ─── Major detail (trang ngành phía sinh viên, Sprint 31) ──────────────────

export interface MajorTermCourse {
  id: number;
  code: string;
  slug: string;
  name: string;
  credits: number;
  is_required: boolean;
}

export interface MajorTerm {
  id: number;
  name: string;
  slug: string;
  order: number;
  course_count: number;
  courses: MajorTermCourse[];
}

export interface MajorDetail {
  id: number;
  code: string;
  slug: string;
  name: string;
  description: string;
  is_published: boolean;
  version: number;
  is_latest: boolean;
  program_slug: string;
  program_name: string;
  terms: MajorTerm[];
  created_at: string;
  updated_at: string;
}

// ─── API calls ──────────────────────────────────────────────────────────────

export async function fetchCourses(params?: {
  published?: boolean;
  tag?: string;
  page?: number;
}): Promise<Paginated<CourseListItem>> {
  const query: Record<string, string | number | boolean> = {};
  if (params?.published !== undefined) query.is_published = params.published;
  if (params?.tag) query.tags__slug = params.tag;
  if (params?.page) query.page = params.page;

  const res = await apiClient.get<Paginated<CourseListItem>>("/catalog/courses/", {
    params: query,
  });
  return res.data;
}

export async function fetchCourseDetail(slug: string): Promise<CourseDetail> {
  const res = await apiClient.get<CourseDetail>(`/catalog/courses/${slug}/`);
  return res.data;
}

export async function fetchLessonDetail(slug: string): Promise<LessonDetail> {
  const res = await apiClient.get<LessonDetail>(`/catalog/lessons/${slug}/`);
  return res.data;
}

export async function fetchMajorDetail(slug: string): Promise<MajorDetail> {
  const res = await apiClient.get<MajorDetail>(`/catalog/majors/${slug}/`);
  return res.data;
}

// ─── Hooks ─────────────────────────────────────────────────────────────────

export function useCourses(params?: Parameters<typeof fetchCourses>[0]) {
  return useQuery({
    queryKey: ["courses", params],
    queryFn: () => fetchCourses(params),
  });
}

export function useCourseDetail(slug: string | undefined) {
  return useQuery({
    queryKey: ["course", slug],
    queryFn: () => fetchCourseDetail(slug!),
    enabled: !!slug,
  });
}

export function useLessonDetail(slug: string | undefined) {
  return useQuery({
    queryKey: ["lesson", slug],
    queryFn: () => fetchLessonDetail(slug!),
    enabled: !!slug,
  });
}

export function useMajorDetail(slug: string | undefined) {
  return useQuery({
    queryKey: ["major", slug],
    queryFn: () => fetchMajorDetail(slug!),
    enabled: !!slug,
  });
}
